import { useState, useEffect, useRef, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { CircleNotch, Eye, EyeSlash, ArrowRight, ArrowLeft, PencilSimple } from '@phosphor-icons/react'
import { useAuth } from '../contexts/AuthContext'
import PageTransition from '../components/PageTransition'
import Navbar from '../components/Navbar'
import ShimmerButton from '../components/magicui/ShimmerButton'
import BrandLogo from '../components/website/BrandLogo'

/**
 * Vercel / Linear two-step sign-in flow (2026-04-18 redesign).
 *
 * Step 1 — Email:
 *   - Single text input, full width, centred.
 *   - "Continuer" button advances to step 2.
 *   - Link to /signup below.
 *
 * Step 2 — Password:
 *   - Email shown as a pill at the top with an inline "Modifier" action
 *     that returns to step 1 WITHOUT clearing the email value (data
 *     retention Mustapha asked for — never re-type what you already
 *     typed).
 *   - Password input auto-focuses on mount.
 *   - "Se connecter" submits. Forgot-password link beneath.
 *
 * Framer AnimatePresence handles the slide choreography: email slides
 * LEFT out, password slides in FROM the right. Back navigation reverses
 * the direction. Reduced-motion users get a pure crossfade.
 *
 * Zero gradient CTAs, zero card-in-card chrome — the whole form rests
 * on the page plane. Anti-AI-slop compliant.
 *
 * i18n: all strings via `useTranslation('auth')`. Persistence of the
 * email value + rememberMe flag across the step transition is what
 * Mustapha called out as critical.
 */
export default function Login() {
  const { t } = useTranslation('auth')
  const { signIn: authSignIn } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [step, setStep] = useState<'email' | 'password'>('email')
  const [direction, setDirection] = useState<1 | -1>(1) // 1 = forward, -1 = back
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(true)
  const [loading, setLoading] = useState(false)
  const passwordInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (searchParams.get('expired')) {
      toast.error(t('login.session_expired'))
    }
    // Pre-fill email when returning from signup success (query string)
    const prefill = searchParams.get('email')
    if (prefill) setEmail(prefill)
  }, [searchParams, t])

  useEffect(() => {
    if (step === 'password' && passwordInputRef.current) {
      // Autofocus password on step-in so typing starts immediately.
      passwordInputRef.current.focus()
    }
  }, [step])

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

  const handleEmailNext = (e: FormEvent) => {
    e.preventDefault()
    setEmailError('')
    if (!isValidEmail(email)) {
      setEmailError(t('login.email_invalid'))
      return
    }
    setDirection(1)
    setStep('password')
  }

  const handleBackToEmail = () => {
    setDirection(-1)
    setError('')
    setStep('email')
  }

  const handleSwitchToSignup = () => {
    const trimmed = email.trim()
    const target = trimmed.length > 0 && isValidEmail(trimmed)
      ? `/signup?email=${encodeURIComponent(trimmed)}`
      : '/signup'
    // Soft client-side nav — the App-level AnimatePresence handles the
    // cross-fade between /login and /signup so there's no white flash.
    navigate(target)
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      localStorage.setItem('betterclose-remember-me', rememberMe ? 'true' : 'false')
      const { error } = await authSignIn(email, password)

      if (error) {
        setError(error.message)
        toast.error(t('login.error_invalid_credentials'))
      } else {
        toast.success(t('login.success'))
        // Hard reload — fresh Supabase client + React state.
        window.location.href = '/dashboard'
        return
      }
    } catch (_err) {
      setError(t('login.error_generic'))
      toast.error(t('login.error_toast_generic'))
    } finally {
      setLoading(false)
    }
  }

  // Slide direction for AnimatePresence — enter from direction, exit to opposite.
  const stepVariants = {
    enter: (dir: 1 | -1) => ({ opacity: 0, x: dir * 40 }),
    center: { opacity: 1, x: 0 },
    exit: (dir: 1 | -1) => ({ opacity: 0, x: dir * -40 }),
  }

  return (
    <PageTransition>
      <div className="min-h-[100dvh] bg-[var(--bn-bg-page)] flex flex-col relative transition-colors duration-300">
        <Navbar variant="auth" />

        {/* Main content — perfectly centred, generous vertical breathing. */}
        <main className="relative z-10 flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 pb-12 pt-4 sm:pt-8">
          <div className="w-full max-w-[380px]">

            {/* Hero heading — constant across both steps, doesn't re-animate. */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="text-center mb-10 sm:mb-12"
            >
              <h1 className="text-2xl sm:text-3xl font-light tracking-tight text-[var(--bn-text-primary)] mb-2">
                {t('login.heading_prefix')}{' '}
                <BrandLogo variant="hero" className="inline-flex align-baseline text-[var(--bn-text-primary)]" />
              </h1>
              <p className="text-sm text-[var(--bn-text-subtle)] font-light">
                {step === 'email' ? t('login.step_email_label') : t('login.step_password_label')}
              </p>
            </motion.div>

            {/* Step body — AnimatePresence swaps between the two sub-forms. */}
            <AnimatePresence mode="wait" custom={direction}>
              {step === 'email' && (
                <motion.form
                  key="step-email"
                  custom={direction}
                  variants={stepVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                  onSubmit={handleEmailNext}
                  className="space-y-5"
                  autoComplete="on"
                >
                  <div>
                    <label htmlFor="email" className="sr-only">
                      {t('login.email_label')}
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      autoFocus
                      required
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value)
                        if (emailError) setEmailError('')
                      }}
                      aria-invalid={Boolean(emailError) || undefined}
                      aria-describedby={emailError ? 'email-error' : undefined}
                      placeholder={t('login.email_placeholder')}
                      className={`w-full bg-[var(--bn-bg-surface)] border ${
                        emailError
                          ? 'border-[var(--bn-state-error-border)] focus:border-[var(--bn-state-error-text)]'
                          : 'border-[var(--bn-border)] hover:border-[var(--bn-border-strong)] focus:border-[var(--bn-accent-border)]'
                      } rounded-[var(--bn-radius-action)] px-4 py-3.5 text-base text-[var(--bn-text-primary)] placeholder-[var(--bn-text-muted)] font-light transition-colors duration-150 focus:outline-none focus-visible:[box-shadow:var(--bn-focus-ring)]`}
                    />
                    {emailError && (
                      <p id="email-error" className="mt-2 text-xs text-[var(--bn-state-error-text)] font-light">
                        {emailError}
                      </p>
                    )}
                  </div>

                  <ShimmerButton
                    type="submit"
                    className="w-full py-3.5 text-base font-medium shadow-[var(--bn-accent-glow)] hover:shadow-[var(--bn-accent-glow-strong)]"
                  >
                    {t('login.continue_cta')}
                    <ArrowRight className="h-4 w-4" weight="bold" />
                  </ShimmerButton>

                  <p className="pt-1 text-center text-[11px] leading-relaxed text-[var(--bn-text-subtle)] font-light">
                    {t('login.continue_accepts')}{' '}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-2 decoration-[var(--bn-border-strong)] hover:text-[var(--bn-text-body)] transition-colors"
                    >
                      {t('login.terms_link')}
                    </a>{' '}
                    {t('login.terms_and')}{' '}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-2 decoration-[var(--bn-border-strong)] hover:text-[var(--bn-text-body)] transition-colors"
                    >
                      {t('login.privacy_link')}
                    </a>
                    .
                  </p>

                  <div className="pt-4 text-center text-sm font-light text-[var(--bn-text-subtle)]">
                    {t('login.divider_new_user')}{' '}
                    <button
                      type="button"
                      onClick={handleSwitchToSignup}
                      className="text-[var(--bn-accent)] hover:text-[var(--bn-accent-deep)] font-medium transition-colors"
                    >
                      {t('login.signup_cta')}
                    </button>
                  </div>
                </motion.form>
              )}

              {step === 'password' && (
                <motion.form
                  key="step-password"
                  custom={direction}
                  variants={stepVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                  onSubmit={handleSubmit}
                  className="space-y-5"
                  autoComplete="on"
                >
                  {/* Email pill — read-only display of the step-1 email
                      with an inline "Modifier" button that returns to
                      step 1 without clearing the value. Premium touch:
                      the user never retypes. */}
                  <button
                    type="button"
                    onClick={handleBackToEmail}
                    className="w-full group flex items-center justify-between gap-3 bg-[var(--bn-bg-surface)] border border-[var(--bn-border)] hover:border-[var(--bn-border-strong)] rounded-[var(--bn-radius-action)] px-4 py-3 text-left transition-colors"
                    aria-label={t('login.change_email')}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <ArrowLeft className="h-3.5 w-3.5 shrink-0 text-[var(--bn-text-subtle)] group-hover:text-[var(--bn-text-body)] transition-colors" weight="bold" />
                      <span className="truncate text-sm text-[var(--bn-text-primary)] font-light">
                        {email}
                      </span>
                    </span>
                    <span className="flex items-center gap-1 shrink-0 text-xs text-[var(--bn-accent)] opacity-80 group-hover:opacity-100 transition-opacity">
                      <PencilSimple className="h-3 w-3" weight="bold" />
                      {t('login.change_email')}
                    </span>
                  </button>

                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="rounded-[var(--bn-radius-action)] border border-[var(--bn-state-error-border)] bg-[var(--bn-state-error-bg)] px-4 py-3"
                    >
                      <p className="text-xs font-light text-[var(--bn-state-error-text)]">{error}</p>
                    </motion.div>
                  )}

                  <div>
                    <label htmlFor="password" className="sr-only">
                      {t('login.password_label')}
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        ref={passwordInputRef}
                        placeholder="••••••••••"
                        className="w-full bg-[var(--bn-bg-surface)] border border-[var(--bn-border)] hover:border-[var(--bn-border-strong)] focus:border-[var(--bn-accent-border)] rounded-[var(--bn-radius-action)] px-4 pr-12 py-3.5 text-base text-[var(--bn-text-primary)] placeholder-[var(--bn-text-muted)] font-light transition-colors duration-150 focus:outline-none focus-visible:[box-shadow:var(--bn-focus-ring)]"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? t('login.hide_password', { defaultValue: 'Masquer' }) : t('login.show_password', { defaultValue: 'Afficher' })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-lg text-[var(--bn-text-subtle)] hover:text-[var(--bn-text-body)] hover:bg-[var(--bn-bg-surface-hover)] transition-colors"
                      >
                        {showPassword ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-4">
                    <label className="flex items-center gap-2.5 cursor-pointer group select-none">
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={rememberMe}
                        onClick={() => setRememberMe(!rememberMe)}
                        className={`relative w-[18px] h-[18px] rounded-[5px] border transition-colors duration-150 flex items-center justify-center ${
                          rememberMe
                            ? 'bg-[var(--bn-accent)] border-[var(--bn-accent)]'
                            : 'bg-[var(--bn-bg-surface-weak)] border-[var(--bn-border-strong)] group-hover:border-[var(--bn-accent-border-bold)]'
                        }`}
                      >
                        <motion.svg viewBox="0 0 12 12" className="w-3 h-3" initial={false}>
                          <motion.path
                            d="M2.5 6L5 8.5L9.5 3.5"
                            fill="none"
                            stroke="white"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            initial={{ pathLength: 0, opacity: 0 }}
                            animate={{ pathLength: rememberMe ? 1 : 0, opacity: rememberMe ? 1 : 0 }}
                            transition={{ duration: 0.2, ease: 'easeOut' }}
                          />
                        </motion.svg>
                      </button>
                      <span className="text-xs text-[var(--bn-text-muted)] group-hover:text-[var(--bn-text-body)] transition-colors">
                        {t('login.remember_me')}
                      </span>
                    </label>
                    <Link
                      to="/forgot-password"
                      className="text-xs text-[var(--bn-accent)] hover:text-[var(--bn-accent-deep)] transition-colors"
                    >
                      {t('login.forgot_password')}
                    </Link>
                  </div>

                  <ShimmerButton
                    type="submit"
                    disabled={loading}
                    className="w-full py-3.5 text-base font-medium shadow-[var(--bn-accent-glow)] hover:shadow-[var(--bn-accent-glow-strong)]"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <CircleNotch className="w-4 h-4 animate-spin" />
                        {t('login.submit_loading')}
                      </span>
                    ) : (
                      <>
                        {t('login.submit_cta')}
                        <ArrowRight className="h-4 w-4" weight="bold" />
                      </>
                    )}
                  </ShimmerButton>
                </motion.form>
              )}
            </AnimatePresence>

            {/* Back to home — hairline, always bottom. */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="mt-10 sm:mt-12 text-center"
            >
              <Link
                to="/"
                className="text-xs text-[var(--bn-text-subtle)] hover:text-[var(--bn-text-body)] transition-colors font-light"
              >
                {t('login.back_home')}
              </Link>
            </motion.div>
          </div>
        </main>
      </div>
    </PageTransition>
  )
}
